import { Dispatch, compose } from 'redux';
/**
 *
 * CheckoutPage
 *
 */
import { Col, Row } from 'antd';
import Button from 'components/Button/Button';
import Checkout from 'components/Checkout/Checkout';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import { push } from 'connected-react-router';
import { refreshCart, updateStoreBasicInfo } from 'containers/MainLayout/actions';
import {
  selectCart,
  selectOrderDisabled,
  selectPendingApprovalMode,
  selectStore,
} from 'containers/MainLayout/selectors';
import { GQuery, useLazyQuerySaga } from 'hooks/useQuerySaga';
import React, { FC, Fragment, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import translations from 'translations';
import {
  Location,
  OrderDisabledReason,
  QuerySearchNewWardForStoreArgs,
  UpdateStoreBasicInfoInput,
  Ward,
} from 'types/schema';
import injectReducer from 'utils/injectReducer';
import injectSaga from 'utils/injectSaga';
import media from 'utils/mediaStyle';
import { searchNewWardForStore } from 'utils/query';
import { applyVoucher, setCheckoutStatus, trackOrdersCompletedById } from './actions';
import EmptyCartImage from './icons/emptyCart.svg';
import messages from './messages';
import reducer from './reducer';
import saga from './saga';
import selectCheckoutPage, { selectCheckoutStatus } from './selectors';
import { RootState } from './types';

const OrderCompleteModal = React.lazy(() => import('./components/OrderCompleteModal'));
const ForceUpdateNewAddressModal = React.lazy(() => import('./components/ForceUpdateNewAddressModal'));

interface OwnProps {
  checkoutpage: any;
  cart: any;
  isPendingApproval: boolean;
  checkoutStatus: any;
  orderDisabled: {
    orderDisabled: boolean;
    message: string;
    reason?: OrderDisabledReason;
  };
  store: any;
}

interface StateProps {}

interface DispatchProps {
  onGoHome: () => void;
  onGoToOrderDetail: (id: string) => void;
  onCloseOrderCompleteModal: () => void;
  onTrackOrdersCompleted: (ordersId) => void;
  onApplyVoucher: (data: any) => void;
  updateStoreBasicInfo: (data: Omit<UpdateStoreBasicInfoInput, 'storeUuid'>, cb?: (success: boolean) => void) => void;
  refreshCart: () => void;
  goToHome: () => void;
}

type Props = StateProps & DispatchProps & OwnProps;

const Container = styled.div`
  margin-top: 99.66px;
  display: flex;
  flex-direction: column;
  ${media.md`
      margin-top: 105.66px;
      padding-top: ${({ empty }) => (empty ? '28px' : '0')};
  `};

  align-items: ${({ center }) => (center ? 'center' : 'unset')};
  padding-top: ${({ empty }) => (empty ? '14px' : '0')};
`;

const CartEmptyTitle = styled.div`
  margin-top: 7px;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: #111827;
`;

const CartEmptyDescription = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #4b5563;
  width: 100%;
  text-align: center;
  margin-top: 4px;
  padding: 0 16px;

  ${media.md`
    padding: 0px;
  `}
`;

const ButtonActionsContainer = styled.div`
  width: 100%;
  margin-top: 24px;
  display: flex;
  justify-content: center;
  gap: 12px;
`;

const GoToFavoriteButton = styled(Button)`
  width: 100% !important;
  max-width: 190px;
  height: 44px;
  font-weight: 500 !important;
  font-size: 16px !important;
  box-shadow: none !important;
  color: #2f382c !important;
`;

const GoToMarketButton = styled(Button)`
  width: 100% !important;
  max-width: 190px;
  height: 44px;
  font-weight: 500 !important;
  font-size: 16px !important;
  box-shadow: none !important;
  background: #43a047 !important;
  color: #fff !important;
  border: none !important;
`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 44px;

  ${media.md`
    margin-top: 112px;
  `};
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
  justify-content: space-between;
  padding: 0 16px;

  ${media.lg`
    margin-bottom: 20px;
    align-items: flex-end;
    padding: 0px;
  `};
`;

const HeaderTitle = styled.div`
  font-size: 16px;
  line-height: 24px;
  color: #2f382c;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;

  ${media.md`
    font-size: 20px;
    line-height: 28px;
  `};
`;

const NumberOfProducts = styled.span`
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
  color: #2f382c;

  ${media.md`
    font-size: 16px;
    line-height: 20px;
  `};
`;

const CheckoutPage: FC<Props> = (props) => {
  const {
    checkoutpage,
    onGoToOrderDetail,
    onGoHome,
    cart,
    checkoutStatus,
    isPendingApproval,
    orderDisabled,
    onTrackOrdersCompleted,
    onApplyVoucher,
    store,
    updateStoreBasicInfo,
    refreshCart,
    goToHome,
    onCloseOrderCompleteModal,
  } = props;

  const [openForceUpdateAddressModal, setOpenForceUpdateAddressModal] = useState(false);
  const loggedIn = !!window.localStorage.getItem('token');

  const [fetchNewWardForStore, { loading, data: recommendNewWardForStore }] = useLazyQuerySaga<
    GQuery<'searchNewWardForStore'>,
    QuerySearchNewWardForStoreArgs
  >(searchNewWardForStore);

  const isCheckoutSuccess = useMemo(
    () => checkoutStatus && checkoutStatus.status === 'CHECKOUT_SUCCESS',
    [checkoutStatus],
  );

  const handleTrackOrderCompleted = useCallback(
    (orders) => {
      if (orders.length) {
        onTrackOrdersCompleted(orders.map(({ id }) => id));
      }
    },
    [onTrackOrdersCompleted],
  );

  // Effects
  useEffect(() => {
    if (
      (orderDisabled?.orderDisabled && orderDisabled?.reason === OrderDisabledReason.OutdatedWard) ||
      store?.moq < 0
    ) {
      setOpenForceUpdateAddressModal(true);
      fetchNewWardForStore({
        variables: {
          storeUuid: store?.id,
        },
      });
    } else {
      setOpenForceUpdateAddressModal(false);
    }
  }, [orderDisabled, store]);

  useEffect(() => {
    if (!cart?.cartSet?.length && !cart?.loading) {
      onApplyVoucher({});
    }
  }, [cart?.loading, cart?.cartSet?.length]);

  const isEmpty = !cart?.cartSet?.[0]?.items?.length && !cart?.loading;

  return (
    <Fragment>
      {isEmpty ? (
        <Container empty>
          <Row gutter={[24, 8]}>
            <Col lg={16} xs={24}>
              <HeaderContainer>
                <HeaderTitle>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    onClick={goToHome}
                    style={{ cursor: 'pointer' }}
                  >
                    <path
                      d="M12.6005 5.59961L7.05072 11.1494H18.9999V12.8486H7.05072L12.6005 18.3984L11.3993 19.5996L4.39935 12.5996C4.0674 12.2677 4.0674 11.7304 4.39935 11.3984L11.3993 4.39844L12.6005 5.59961Z"
                      fill="#4B5563"
                    />
                  </svg>
                  {translations(messages.headerStep1)}{' '}
                  <NumberOfProducts>
                    ({Number(cart?.cartSet?.[0]?.items?.length || 0)}{' '}
                    {translations(cart?.cartSet?.[0]?.items?.length > 1 ? messages.products : messages.product)})
                  </NumberOfProducts>
                </HeaderTitle>
              </HeaderContainer>
            </Col>
          </Row>
          <Empty>
            <SVGIcon src={EmptyCartImage} width={100} height={100} />
            <CartEmptyTitle>
              <FormattedMessage {...messages.emptyCart} />
            </CartEmptyTitle>
            <CartEmptyDescription>
              {loggedIn ? (
                <FormattedMessage {...messages.emptyCartDescription} />
              ) : (
                <FormattedMessage {...messages.emptyCartDescriptionNonSignIn} />
              )}
            </CartEmptyDescription>
            <ButtonActionsContainer>
              {loggedIn && (
                <Link to="/favorite">
                  <GoToFavoriteButton type="default">
                    <FormattedMessage {...messages.goToFavorites} />
                  </GoToFavoriteButton>
                </Link>
              )}
              <Link to="/">
                <GoToMarketButton type="primary">
                  <FormattedMessage {...messages.goToHome} />
                </GoToMarketButton>
              </Link>
            </ButtonActionsContainer>
          </Empty>
        </Container>
      ) : (
        <Container>
          <Checkout
            orderDraftToken={checkoutpage?.orderDraftToken}
            orderDisabled={orderDisabled.orderDisabled}
            orderDisabledMessage={orderDisabled.message}
            orderDisabledReason={orderDisabled.reason}
            setOpenForceUpdateAddressModal={setOpenForceUpdateAddressModal}
          />
        </Container>
      )}
      <Suspense fallback={null}>
        <OrderCompleteModal
          isOpen={isCheckoutSuccess}
          isPendingApproval={isPendingApproval}
          onGoToOrderDetail={onGoToOrderDetail}
          onGoHome={onGoHome}
          onClose={onCloseOrderCompleteModal}
          orders={checkoutStatus?.orders || []}
          onTrackOrdersCompleted={handleTrackOrderCompleted}
        />
      </Suspense>
      {openForceUpdateAddressModal && (
        <Suspense fallback={null}>
          <ForceUpdateNewAddressModal
            openModal={openForceUpdateAddressModal}
            currentStoreAddress={{
              ...(store?.location as Location),
              noteForAddress: store?.addressNote?.trim() || '',
            }}
            storeName={store?.name || ''}
            onClose={() => {
              setOpenForceUpdateAddressModal(false);
            }}
            recommendNewWardForStore={(recommendNewWardForStore?.data as Ward[]) || []}
            updateStoreBasicInfo={updateStoreBasicInfo}
            isFetchingWard={loading}
          />
        </Suspense>
      )}
    </Fragment>
  );
};

const mapStateToProps = createStructuredSelector<RootState, StateProps>({
  checkoutpage: selectCheckoutPage(),
  cart: selectCart(),
  checkoutStatus: selectCheckoutStatus(),
  isPendingApproval: selectPendingApprovalMode(),
  orderDisabled: selectOrderDisabled(),
  store: selectStore(),
});

function mapDispatchToProps(dispatch: Dispatch): DispatchProps {
  return {
    onGoHome: () => {
      dispatch(setCheckoutStatus({ status: 'DRAFT' }));
      dispatch(push('/'));
    },
    onCloseOrderCompleteModal: () => dispatch(setCheckoutStatus({ status: 'DRAFT' })),
    onGoToOrderDetail: (id: string) => {
      dispatch(push(`/orders/${id}`));
      dispatch(
        setCheckoutStatus({
          status: 'DRAFT',
        }),
      );
    },
    onTrackOrdersCompleted: (orderIds) => dispatch(trackOrdersCompletedById(orderIds)),
    onApplyVoucher: (data: any) => dispatch(applyVoucher(data)),
    updateStoreBasicInfo: (data: Omit<UpdateStoreBasicInfoInput, 'storeUuid'>, cb?: (success: boolean) => void) =>
      dispatch(updateStoreBasicInfo(data, cb)),
    refreshCart: () => dispatch(refreshCart()),
    goToHome: () => dispatch(push('/')),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

// <OwnProps> restricts access to the HOC's other props. This component must not do anything with reducer hoc
const withReducer = injectReducer<OwnProps>({
  key: 'checkoutPage',
  reducer: reducer,
});
// <OwnProps> restricts access to the HOC's other props. This component must not do anything with saga hoc
const withSaga = injectSaga<OwnProps>({ key: 'checkoutPage', saga: saga });

export default compose(withReducer, withSaga, withConnect)(CheckoutPage);
